"use client";

import { type ReactNode, useCallback, useEffect, useState } from "react";
import type { ChatMessage } from "@/app/ai-agent/types";
import CopyIcon from "@/assets/images/ai-copy.svg";
import EditIcon from "@/assets/images/ai-edit.svg";
import RegenerateIcon from "@/assets/images/ai-regenerate.svg";
import SparkleIcon from "@/assets/images/cashless-ai-v2.svg";
import { useTypewriter } from "@/hooks/ai-agent/useTypewriter";
import { splitStreamedMarkdown } from "@/lib/ai-gateway/markdownStream";
import { cn } from "@/lib/utils";
import { useAlert } from "@/Provider/AlertProvider/AlertProvider";
import renderInlineMarkdown from "@/utils/renderInlineMarkdown";
import { AiMarkdownBoundary } from "./AiMarkdown";

/** A small ghost action button shown with a message (regenerate / copy / edit). */
function ActionIcon({
  label,
  onClick,
  disabled,
  children,
}: {
  label: string;
  onClick?: () => void;
  disabled?: boolean;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
      className="flex size-7 shrink-0 items-center justify-center rounded-md text-text-tertiary transition-colors hover:bg-bg-weak-50 hover:text-text-secondary disabled:pointer-events-none disabled:opacity-40"
    >
      {children}
    </button>
  );
}

export interface AiMessageProps {
  message: ChatMessage;
  /** Load this (user) message's text back into the composer to edit + re-send. */
  onEdit?: (message: ChatMessage) => void;
  /** Re-send this (user) message's prompt as a fresh turn. */
  onRegenerate?: (message: ChatMessage) => void;
  /** A turn is in flight — disable edit/regenerate so they can't stack turns. */
  busy?: boolean;
  /**
   * Voice mode: keep an unrevealed assistant reply on the "Thinking…" shimmer
   * until the agent is audibly speaking, so text and voice start together.
   * Only delays the START of the reveal — an in-progress reveal never pauses —
   * and a fallback timer force-reveals if speech never arrives.
   */
  revealHeld?: boolean;
  /** "speech" paces the typewriter to read-aloud speed (voice mode). */
  revealPace?: "fast" | "speech";
}

/**
 * Shimmering "Thinking…" label shown while an assistant reply is streaming but
 * still empty — a brand-primary light sweeps across the text (see the
 * `.ai-thinking-shimmer` keyframes in globals.css).
 */
function ThinkingIndicator() {
  return (
    <span
      role="status"
      aria-label="Assistant is thinking"
      className="ai-thinking-shimmer select-none py-0.5 paragraph-sm font-medium"
    >
      Thinking…
    </span>
  );
}

/**
 * A single chat message (Figma 213:201953). A **user** message is a right-aligned
 * neutral bubble with regenerate/copy/edit actions; an **assistant** message is
 * the brand sparkle beside a soft card holding the reply, with a copy action on
 * the card's right edge. While the assistant reply is still streaming with no
 * text yet, a typing indicator stands in for the empty bubble.
 */
export function AiMessage({
  message,
  onEdit,
  onRegenerate,
  busy = false,
  revealHeld = false,
  revealPace = "fast",
}: AiMessageProps) {
  const { showSuccess, showError } = useAlert();
  // Only a bubble that mounted while streaming should type out; history renders
  // in full. `enabled` is captured once at mount and never changes.
  const [animate] = useState(
    () => message.role === "assistant" && message.status === "streaming",
  );

  // Safety valve for the voice-synced hold: if the turn has settled but the
  // agent never becomes audible (TTS hiccup), reveal anyway after a beat.
  const [forceReveal, setForceReveal] = useState(false);
  const held = animate && revealHeld && !forceReveal;
  useEffect(() => {
    if (!held || message.status === "streaming") return;
    const t = setTimeout(() => setForceReveal(true), 4000);
    return () => clearTimeout(t);
  }, [held, message.status]);

  const displayed = useTypewriter(message.content, animate, held, revealPace);

  // Nothing is still arriving once the turn has settled AND the typewriter has
  // caught up — the same predicate the caret below uses, inverted.
  const settled =
    message.status !== "streaming" &&
    displayed.length >= message.content.length;

  // Only completed lines are safe to parse as markdown; the trailing partial
  // line keeps typing as plain text. See lib/ai-gateway/markdownStream.
  const { rendered, pending } = splitStreamedMarkdown(displayed, {
    complete: settled,
  });

  const canCopy = message.content.trim().length > 0;

  const copyMessage = useCallback(async () => {
    if (!canCopy) return;
    try {
      await navigator.clipboard.writeText(message.content);
      showSuccess("Copied to clipboard");
    } catch {
      showError("Couldn't copy to clipboard");
    }
  }, [canCopy, message.content, showError, showSuccess]);

  if (message.role === "user") {
    return (
      <div className="flex flex-col items-end gap-1.5">
        <div className="max-w-[78%] whitespace-pre-line rounded-2xl border border-neutral-200/60 bg-white px-4 py-2.5 paragraph-sm text-text-primary">
          {message.content}
        </div>
        <div className="flex items-center gap-0.5">
          <ActionIcon
            label="Regenerate"
            disabled={busy || !onRegenerate}
            onClick={() => onRegenerate?.(message)}
          >
            <RegenerateIcon className="size-5" />
          </ActionIcon>
          <ActionIcon label="Copy" onClick={() => void copyMessage()}>
            <CopyIcon className="size-5" />
          </ActionIcon>
          <ActionIcon
            label="Edit"
            disabled={busy || !onEdit}
            onClick={() => onEdit?.(message)}
          >
            <EditIcon className="size-5" />
          </ActionIcon>
        </div>
      </div>
    );
  }

  return (
    <div className="relative flex flex-col gap-2">
      {/* The sparkle sits OUTSIDE the card, in the message column's left
          gutter, so the bubble still spans the column's full width and its
          edges line up with the composer below. Absolutely positioned rather
          than a flex sibling: a sibling occupies track, which is what inset the
          card by 20px (size-5) + 10px gap and misaligned it against the input.
          `top-3` matches the card's `py-3`, so the mark rides the reply's first
          line. The scroll container's `px-8` reserves the gutter it sits in. */}
      <SparkleIcon
        className={cn(
          "-left-[30px] absolute top-3 size-5 text-primary-500",
          message.status === "streaming" && "ai-twinkle",
        )}
      />
      {/* Copy sits at the card's bottom-right corner (`items-end`). `min-w-0`
          is kept from the upstream fix: it stops the card refusing to shrink
          below a table's min-content width and growing past the thread column.
          `flex-1` is gone with the wrapper row the card used to sit in. */}
      <div className="flex min-w-0 items-end gap-3 rounded-2xl border border-neutral-200/60 bg-white px-4 py-3">
        {/* Nothing revealed yet (still streaming, or held for voice sync) →
            shimmer "Thinking…"; otherwise the paced (typing-out) reply text
            with a caret until fully revealed. */}
        {(message.status === "streaming" || held) && !displayed ? (
          <div className="flex-1">
            <ThinkingIndicator />
          </div>
        ) : (
          // A <div>, NOT a <p>: tables and lists are block elements and are
          // invalid inside a paragraph, which React reports as a hydration
          // error. `whitespace-pre-line` is gone deliberately — remark-breaks
          // emits a real <br/> FOLLOWED by a literal newline, and pre-line
          // would render that newline as a second break, double-spacing every
          // line. `min-w-0` lets the flex child shrink so a wide table
          // scrolls inside the bubble instead of pushing it past its bounds.
          <div className="min-w-0 flex-1 paragraph-sm text-text-primary">
            {rendered && <AiMarkdownBoundary text={rendered} />}
            {pending && renderInlineMarkdown(pending)}
            {(message.status === "streaming" ||
              displayed.length < message.content.length) && (
              <span className="ml-0.5 inline-block h-4 w-px animate-pulse bg-text-tertiary align-text-bottom" />
            )}
          </div>
        )}
        <ActionIcon label="Copy" onClick={() => void copyMessage()}>
          <CopyIcon className="size-5" />
        </ActionIcon>
      </div>
    </div>
  );
}
